import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BG_CARD,
  BORDER_LIGHT,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

interface CongratsMessage {
  id: string;
  title: string;
  body: string | null;
  authorName: string;
  status: string;
  createdAt: string;
}

interface EditionInfo {
  id: string;
  name: string;
}

/**
 * BookletCongratsPage — Lists congratulatory messages from the booklet.
 */
export function BookletCongratsPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<CongratsMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMsg, setSelectedMsg] = useState<CongratsMessage | null>(null);

  useEffect(() => {
    async function fetch() {
      try {
        const ed = await apiClient<EditionInfo>('/weekend-booklet/editions/active');
        if (!ed?.id) { setLoading(false); return; }
        // Congratulatory Messages are GeneralArticles with bookletSection = 'Congratulatory Message'
        const all = await apiClient<(CongratsMessage & { bookletSection?: string | null })[]>(
          `/weekend-booklet/submissions?editionId=${ed.id}&sectionCategory=GeneralArticles`
        );
        const congrats = all.filter((s) => s.bookletSection === 'Congratulatory Message');
        setMessages(congrats);
      } catch {
        setError('Unable to load congratulatory messages.');
      } finally {
        setLoading(false);
      }
    }
    fetch();
  }, []);

  const stripHtml = (html: string | null): string => {
    if (!html) return '';
    return html.replace(/<[^>]+>/g, '').trim();
  };

  if (loading) {
    return <div style={styles.page}><p style={{ color: TEXT_MUTED, textAlign: 'center', padding: '2rem' }}>Loading congratulatory messages…</p></div>;
  }

  return (
    <div style={styles.page}>
      <button style={styles.backBtn} onClick={() => navigate(-1)}>← Back</button>
      <h1 style={styles.title}>🎉 Congratulatory Messages</h1>
      <p style={styles.subtitle}>{messages.length} message{messages.length !== 1 ? 's' : ''}</p>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {!error && messages.length === 0 && (
        <div style={styles.empty}>No congratulatory messages in the current booklet edition yet.</div>
      )}

      <div style={styles.list}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={styles.card}
            onClick={() => setSelectedMsg(selectedMsg?.id === msg.id ? null : msg)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setSelectedMsg(selectedMsg?.id === msg.id ? null : msg)}
          >
            <h3 style={styles.cardTitle}>{msg.title}</h3>
            <p style={styles.cardMeta}>From: {msg.authorName}</p>
            {selectedMsg?.id === msg.id && (
              <p style={styles.cardBody}>
                {stripHtml(msg.body) || 'No message content.'}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '1.5rem', minHeight: '100%', maxWidth: '600px', margin: '0 auto' },
  title: { fontFamily: FONT_HEADING, fontSize: '1.5rem', fontWeight: 700, color: TEXT_DARK, margin: '0.5rem 0 0.25rem' },
  subtitle: { color: TEXT_MUTED, fontSize: '0.85rem', marginBottom: '1rem' },
  backBtn: { background: 'none', border: 'none', color: PRIMARY, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer', padding: '4px 0', marginBottom: '0.5rem', display: 'block' },
  errorBanner: { backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem' },
  empty: { textAlign: 'center' as const, padding: '2rem 1rem', color: TEXT_MUTED, fontSize: '0.9rem' },
  list: { display: 'flex', flexDirection: 'column' as const, gap: '12px' },
  card: { backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '10px', padding: '1rem', display: 'flex', flexDirection: 'column' as const, gap: '4px', cursor: 'pointer', transition: 'box-shadow 0.15s' },
  cardTitle: { fontFamily: FONT_HEADING, fontSize: '1rem', fontWeight: 600, color: TEXT_DARK, margin: 0 },
  cardMeta: { fontSize: '0.8rem', color: TEXT_MUTED, margin: 0 },
  cardBody: { fontSize: '0.85rem', color: TEXT_DARK, margin: '0.5rem 0 0', lineHeight: 1.5, borderTop: `1px solid ${BORDER_LIGHT}`, paddingTop: '0.5rem' },
};
